import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import {
  Button,
  Disclosure as AriaDisclosure,
  DisclosureGroup as AriaDisclosureGroup,
  DisclosurePanel as AriaDisclosurePanel,
  type DisclosureProps as AriaDisclosureProps,
  type DisclosureGroupProps as AriaDisclosureGroupProps,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';

const accordionVariants = cva('', {
  variants: {
    variant: {
      default: 'border-b border-neutral/10',
      bordered: 'rounded-lg border border-neutral/10',
      ghost: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const accordionTriggerVariants = cva(
  [
    'flex w-full items-center justify-between gap-2 text-left',
    'font-normal transition-colors group-data-[expanded]:font-semibold',
    'outline-none',
    'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      size: {
        sm: 'px-1 py-0.75 text-sm',
        md: 'px-4 py-3 text-base',
        lg: 'px-5 py-4 text-lg',
      },
      variant: {
        default: 'hover:bg-neutral/5',
        bordered: 'hover:bg-neutral/5',
        ghost: 'rounded-lg hover:bg-neutral/5',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'default',
    },
  },
);

const accordionContentVariants = cva(
  [
    'overflow-hidden text-sm',
    'transition-all duration-200 ease-out',
    'rac-entering:animate-in rac-entering:fade-in-0',
    'rac-exiting:animate-out rac-exiting:fade-out-0',
  ],
  {
    variants: {
      size: {
        sm: 'px-1.5',
        md: 'px-4 pb-3',
        lg: 'px-5 pb-4',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export type AccordionVariant = VariantProps<
  typeof accordionVariants
>['variant'];
export type AccordionSize = VariantProps<
  typeof accordionTriggerVariants
>['size'];

export interface AccordionProps
  extends
    Omit<AriaDisclosureProps, 'children'>,
    VariantProps<typeof accordionVariants>,
    VariantProps<typeof accordionTriggerVariants> {
  /**
   * Content to display in the trigger button
   */
  trigger: React.ReactNode;
  /**
   * Content to display when expanded
   */
  children: React.ReactNode;
  /**
   * Custom icon to show instead of the default chevron
   */
  icon?: React.ReactNode;
  hideIcon?: boolean;
  ref?: React.RefObject<HTMLDivElement>;
}

export const Accordion: React.FC<AccordionProps> = ({
  className,
  variant,
  size,
  trigger,
  children,
  icon,
  hideIcon = false,
  ref,
  ...props
}) => (
  <AriaDisclosure
    ref={ref}
    className={cn(
      '@Accordion',
      'group',
      accordionVariants({ variant }),
      className,
    )}
    {...props}
  >
    <Button
      slot="trigger"
      className={cn(
        '@AccordionTrigger',
        accordionTriggerVariants({ size, variant }),
      )}
    >
      <span className="flex-1">{trigger}</span>
      {!hideIcon &&
        (icon ?? (
          <ChevronDown className="size-4 shrink-0 text-neutral/50 group-data-[expanded]:scale-[-1]" />
        ))}
    </Button>
    <AriaDisclosurePanel
      className={cn('@AccordionContent', accordionContentVariants({ size }))}
    >
      {children}
    </AriaDisclosurePanel>
  </AriaDisclosure>
);

Accordion.displayName = 'Accordion';

const multipleAccordionVariants = cva('flex flex-col', {
  variants: {
    variant: {
      default: 'divide-y divide-neutral/10',
      bordered:
        'divide-y divide-neutral/10 rounded-lg border border-neutral/10',
      ghost: 'gap-1',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface MultipleAccordionProps
  extends
    Omit<AriaDisclosureGroupProps, 'allowsMultipleExpanded'>,
    VariantProps<typeof multipleAccordionVariants> {
  children: React.ReactNode;
  allowsMultipleExpanded?: boolean;
  ref?: React.RefObject<HTMLDivElement>;
}

export const MultipleAccordion: React.FC<MultipleAccordionProps> = ({
  className,
  variant,
  children,
  allowsMultipleExpanded = true,
  ref,
  ...props
}) => (
  <AriaDisclosureGroup
    ref={ref}
    allowsMultipleExpanded={allowsMultipleExpanded}
    className={cn(
      '@MultipleAccordion',
      multipleAccordionVariants({ variant }),
      className,
    )}
    {...props}
  >
    {children}
  </AriaDisclosureGroup>
);

MultipleAccordion.displayName = 'MultipleAccordion';
